import React from 'react'
import { WellbeingGlyph } from '../../../constants/PulseCheckAnalyticsConstants'
import { useTranslation } from 'react-i18next'
import TrackSurface from '../../../analytics/TrackSurface'
import { ANALYTICS_EVENTS } from '../../../analytics/events'

function PulseCategoryList({ items = [], compareLabel = 'vs last week' }) {
    const { t } = useTranslation()

    if (!items.length) {
        return <div className="mt-5 bg-white rounded-2xl py-8 px-6 text-center text-mudPurple font-medium text-[14px]">{t('thereIsNoDataToShow')}</div>
    }

    return (
        <div
            className="grid gap-4 mt-5"
            style={{
                gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))`,
            }}
        >
            {items.map((item) => (
                <div
                    key={item.id}
                    className="rounded-2xl border bg-white py-5 px-[22px] shadow-sm flex flex-col gap-[10px] relative overflow-hidden"
                    style={{ borderColor: item.borderColor }}
                >
                    <TrackSurface event={ANALYTICS_EVENTS.ANALYTICS_PULSE_CATEGORY_VIEWED} contentId={item.id || item.key || item.title} content={item.title} />
                    <div className="absolute top-[-30px] right-[-30px] w-[120px] h-[120px] rounded-full z-0" style={{ background: item.color, opacity: 0.5 }} />
                    <div className="flex items-center gap-[14px] relative z-10">
                        <div className="h-[56px] min-w-[56px] rounded-full flex items-center justify-center" style={{ backgroundColor: item.color }}>
                            <WellbeingGlyph title={item.title} color={item.iconColor} size={32} />
                        </div>

                        <div>
                            <h3 className="font-bold text-[17px] leading-5 text-darkenPurple">{t(item.title)}</h3>
                            <p className="font-medium text-[12px] leading-5 text-mudPurple">{t(item.description)}</p>
                        </div>
                    </div>

                    <div className="flex items-end gap-[10px] relative z-10">
                        <div className="font-bold text-[36px] w" style={{ color: item.iconColor, letterSpacing: -1, lineHeight: 1 }}>
                            {item.progress}
                            <span className="text-[20px]">%</span>
                        </div>
                        {item.score !== null && item.score !== undefined && (
                            <div className="flex flex-col gap-[2px] pb-1">
                                <DeltaPill delta={item.score} />
                                <div className="font-medium text-[10px] text-mudPurple">{t(compareLabel)}</div>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}

export default PulseCategoryList

export const DeltaPill = ({ delta }) => {
    const up = delta >= 0

    const colorClass = up ? 'bg-[rgba(111,204,135,0.16)] text-[#2B8C50]' : 'bg-[rgba(252,76,89,0.14)] text-[#C73340]'

    return (
        <div className={`inline-flex w-fit items-center gap-1 py-[3px] px-[8px] rounded-full font-bold text-[12px] ${colorClass}`}>
            <Icon name={up ? 'arrowUp' : 'arrowDown'} size={12} stroke={2.4} />
            <span>
                {up ? '+' : ''}
                {delta}%
            </span>
        </div>
    )
}

export const Icon = ({ name, size = 18, color = 'currentColor', stroke = 1.8, ...rest }) => {
    const paths = {
        dashboard: (
            <>
                <rect x="3" y="3" width="7" height="9" rx="1.5" />
                <rect x="14" y="3" width="7" height="5" rx="1.5" />
                <rect x="14" y="11" width="7" height="10" rx="1.5" />
                <rect x="3" y="15" width="7" height="6" rx="1.5" />
            </>
        ),
        cards: (
            <>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 10h18" />
            </>
        ),
        classroom: (
            <>
                <path d="M4 19V8l8-4 8 4v11" />
                <path d="M9 19v-6h6v6" />
            </>
        ),
        analytics: (
            <>
                <path d="M4 20V8" />
                <path d="M10 20V4" />
                <path d="M16 20v-9" />
                <path d="M22 20H2" />
            </>
        ),
        chat: (
            <>
                <path d="M21 12a8 8 0 0 1-12.5 6.6L4 20l1.4-4.4A8 8 0 1 1 21 12z" />
            </>
        ),
        users: (
            <>
                <circle cx="9" cy="8" r="3.2" />
                <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
                <circle cx="17" cy="9" r="2.5" />
                <path d="M21.5 19a4.5 4.5 0 0 0-6-4.2" />
            </>
        ),
        tracker: (
            <>
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M3 9h18" />
                <path d="M8 13l2.5 2.5L16 10" />
            </>
        ),
        video: (
            <>
                <rect x="3" y="6" width="13" height="12" rx="2" />
                <path d="M16 10l5-3v10l-5-3z" />
            </>
        ),
        bell: (
            <>
                <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5z" />
                <path d="M10 21a2 2 0 0 0 4 0" />
            </>
        ),
        cog: (
            <>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
            </>
        ),
        logout: (
            <>
                <path d="M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
                <path d="M10 17l-5-5 5-5" />
                <path d="M15 12H5" />
            </>
        ),
        filter: (
            <>
                <path d="M3 5h18l-7 9v6l-4-2v-4z" />
            </>
        ),
        cal: (
            <>
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M16 3v4M8 3v4M3 10h18" />
            </>
        ),
        caret: (
            <>
                <path d="M6 9l6 6 6-6" />
            </>
        ),
        arrowUp: (
            <>
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
            </>
        ),
        arrowDown: (
            <>
                <path d="M12 5v14" />
                <path d="M5 12l7 7 7-7" />
            </>
        ),
        download: (
            <>
                <path d="M12 4v12" />
                <path d="M6 10l6 6 6-6" />
                <path d="M4 20h16" />
            </>
        ),
        info: (
            <>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8h.01" />
                <path d="M11 12h1v5h1" />
            </>
        ),
        search: (
            <>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
            </>
        ),
        sun: (
            <>
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </>
        ),
        pin: (
            <>
                <path d="M12 2l3 6 6 1-4.5 4 1 6L12 16l-5.5 3 1-6L3 9l6-1z" />
            </>
        ),
        close: (
            <>
                <path d="M6 6l12 12M18 6L6 18" />
            </>
        ),
    }
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" {...rest}>
            {paths[name]}
        </svg>
    )
}
